import React from 'react'
import classnames from 'classnames'
import { Route } from 'react-router-dom'
import { observer } from 'mobx-react'
import { Container } from 'typescript-ioc/es5'
import { AppService } from './services/index'
import { MobileRoot } from './mobile-view/root/mobile-root'
import { AppState } from './stores/app'
import { ModuleLoadingScreen } from './components/module-loading-screen/module-loading-screen'
import { Pufferfish } from './pufferfish'
import { PreAuthFeature } from './features/pre-auth/index'
import { CustomView, isMobile, isMobileOnly, MobileOnlyView, osName } from 'react-device-detect'
import './_utils/whitelabeling'
import './styles/globals.scss'
import * as bowser from 'bowser'
import { isApplicationVersionCurrent } from './_utils/is-application-version-current'
/**
 * using legacy airbrake-js library.
 * @airbrake/browser is currently broken and does not send annotations
 * { params, context, session, ... }
 */
import { streamApplicationError } from './_utils/airbrake'
import { WhitelabelConfigModel } from './models/whitelabel-config/whitelabel-config.model'
import { ErrorPage } from './components/error-page/error-page'
import { AbilityContext } from './stores/app-ability'
import { Ability } from '@casl/ability'
import { PlatformUser } from '@pushly/aqe/lib/providers/user-preferences-provider/core/index'
import { PlatformUserContext } from '@pushly/aqe/lib/providers/user-preferences-provider/internal'

interface IProps {
    whiteLabel: WhitelabelConfigModel
    user: PlatformUser
}

interface IState {
    hasError: boolean
    error: any
    errorInfo: any
}

@observer
export class App extends React.Component<IProps, IState> {
    public static contextType = PlatformUserContext
    public context!: React.ContextType<typeof PlatformUserContext>

    private appState: AppState
    private appService: AppService
    private unsubscribeSidebar?: () => void

    public constructor(props: IProps) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)

        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
        }
    }

    public componentDidMount(): void {
        this.ensurePushlyHost()
        this.watchForVersionChange()
    }

    public componentDidCatch(error: any, info: any): void {
        this.setState({
            hasError: true,
            error,
            errorInfo: info,
        })

        this.handleApplicationError(error, info)
    }

    public render() {
        return (
            <AbilityContext.Provider value={this.appState.abilityStore.abilities ?? new Ability()}>
                <div
                    id="pufferfish"
                    className={classnames(
                        'module',
                        'app',
                        isMobile ? 'mv' : 'bv',
                        osName.toLowerCase(),
                        {
                            authenticated: this.appState.isAuthenticated,
                            'sidebar-open': this.context.preferences.state.bundle?.user?.unscoped?.sidebar_expanded,
                            'sidebar-closed': !this.context.preferences.state.bundle?.user?.unscoped?.sidebar_expanded,
                        },
                        bowser.name.toLowerCase(),
                        `${bowser.name.toLowerCase()}__${bowser.version}`,
                    )}
                    ref={this.setAppContainer}
                >
                    <ModuleLoadingScreen loading={this.appState.appIsLoading} />
                    {this.state.hasError ? (
                        <ErrorPage errorCode={500} error={this.state.error} info={this.state.errorInfo} />
                    ) : (
                        <>
                            <CustomView condition={!isMobileOnly} viewClassName="device-wrapper bv-wrapper">
                                <>
                                    <Route component={PreAuthFeature} />
                                    <Route component={Pufferfish} />
                                </>
                            </CustomView>
                            <MobileOnlyView viewClassName="device-wrapper mv-wrapper">
                                <>
                                    <Route component={PreAuthFeature} />
                                    <Route component={MobileRoot} />
                                </>
                            </MobileOnlyView>
                        </>
                    )}
                </div>
            </AbilityContext.Provider>
        )
    }

    protected setAppContainer = (element: any) => {
        this.appService.appContainerRef = element
    }

    protected ensurePushlyHost() {
        const platformRgx = /platform\.p-n\.io/i

        if (process.env.NODE_ENV === 'production' && platformRgx.test(location.href)) {
            window.location.href = location.href.replace(platformRgx, 'platform.pushly.com')
        }
    }

    protected watchForVersionChange = async () => {
        const isCurrent = await isApplicationVersionCurrent()
        if (isCurrent === false) {
            this.appState.isVersionCurrent = false
        } else {
            setTimeout(this.watchForVersionChange, 5 * 60 * 1000)
        }
    }

    protected handleApplicationError(error: any, info: any) {
        streamApplicationError(error, info, this.appState.currentUser, this.appState.currentDomain)
    }
}
